"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { scrollToSection } from "@/lib/smooth-scroll";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, LayoutDashboard, Menu, X } from "lucide-react";
import LogoIcon from "@/assets/images/logo.svg";
import { isAuthenticatedSync } from "@/lib/auth";

interface HeaderProps {
	className?: string;
}

const Header: React.FC<HeaderProps> = ({ className }) => {
	const pathname = usePathname();
	const router = useRouter();
	const [activeSection, setActiveSection] = useState<string>("");
	const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
	const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

	// Handle hash navigation when page loads with a hash
	useEffect(() => {
		if (pathname === "/" && typeof window !== "undefined") {
			const hash = window.location.hash.slice(1); // Remove the # symbol
			if (hash) {
				setActiveSection(hash);
				// Wait for page to fully render before scrolling
				setTimeout(() => {
					scrollToSection(hash);
				}, 500);
			}
		}
	}, [pathname]);

	// Track scroll position to highlight active section
	useEffect(() => {
		if (pathname !== "/") return;

		const handleScroll = () => {
			const sections = ["how-it-works", "payment", "contact"];
			const headerOffset = 250; // Header height + some padding
			const scrollPosition = window.scrollY;
			const viewportHeight = window.innerHeight;
			const viewportCenter = scrollPosition + viewportHeight / 2;

			// Check if we're at the top (Home section)
			if (scrollPosition < 300) {
				setActiveSection("");
				return;
			}

			// Find the section that is currently most prominent in the viewport
			// Batch all layout reads first to avoid forced reflows
			const sectionRects: Array<{ id: string; rect: DOMRect }> = [];
			for (const sectionId of sections) {
				const element = document.getElementById(sectionId);
				if (element) {
					sectionRects.push({
						id: sectionId,
						rect: element.getBoundingClientRect(),
					});
				}
			}

			let activeSectionId = "";
			let bestScore = -1;

			// Process all batched reads
			for (const { id: sectionId, rect } of sectionRects) {
				const elementTop = rect.top + scrollPosition;
				const elementBottom = elementTop + rect.height;
				const elementCenter = elementTop + rect.height / 2;

				// Calculate how much of the section is visible
				const visibleTop = Math.max(scrollPosition, elementTop);
				const visibleBottom = Math.min(
					scrollPosition + viewportHeight,
					elementBottom
				);
				const visibleHeight = Math.max(0, visibleBottom - visibleTop);
				const visibleRatio = visibleHeight / Math.max(rect.height, 1);

				// Score based on:
				// 1. How much of the section is visible (0-1)
				// 2. How close the section center is to viewport center (closer = better)
				// 3. Whether the section has passed the header offset threshold
				const distanceFromViewportCenter = Math.abs(
					elementCenter - viewportCenter
				);
				const distanceScore = Math.max(
					0,
					1 - distanceFromViewportCenter / viewportHeight
				);
				const hasPassedThreshold =
					elementTop <= scrollPosition + headerOffset;

				// Combined score: prioritize visible sections that have passed threshold
				const score =
					visibleRatio * 0.6 +
					distanceScore * 0.3 +
					(hasPassedThreshold ? 0.1 : 0);

				if (score > bestScore && visibleRatio > 0.2) {
					bestScore = score;
					activeSectionId = sectionId;
				}
			}

			// Set the active section if we found a good match
			if (activeSectionId) {
				setActiveSection(activeSectionId);
			}
		};

		// Throttle scroll events and batch layout reads to avoid forced reflows
		let ticking = false;
		const throttledHandleScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					// Batch all layout reads in a single frame to avoid forced reflows
					handleScroll();
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener("scroll", throttledHandleScroll, {
			passive: true,
		});
		handleScroll(); // Check on mount

		return () =>
			window.removeEventListener("scroll", throttledHandleScroll);
	}, [pathname]);

	// Determine if user is logged in (client-side check)
	useEffect(() => {
		if (typeof window === "undefined") return;
		setIsLoggedIn(isAuthenticatedSync());
	}, []);

	const handleSectionClick = (sectionId: string) => {
		setActiveSection(sectionId);
		if (pathname === "/") {
			// Already on home page, just scroll
			// Use larger offset for "how-it-works" to scroll higher
			if (sectionId === "how-it-works") {
				const element = document.getElementById(sectionId);
				if (element) {
					const elementPosition = element.getBoundingClientRect().top;
					const offsetPosition =
						elementPosition + window.pageYOffset - 150; // Larger offset for better visibility
					window.scrollTo({
						top: offsetPosition,
						behavior: "smooth",
					});
				}
			} else {
				scrollToSection(sectionId);
			}
		} else {
			// Navigate to home page with hash, then scroll after navigation completes
			router.push(`/#${sectionId}`);
			// Wait longer for navigation to complete and page to render
			setTimeout(() => {
				if (sectionId === "how-it-works") {
					const element = document.getElementById(sectionId);
					if (element) {
						const elementPosition =
							element.getBoundingClientRect().top;
						const offsetPosition =
							elementPosition + window.pageYOffset - 150;
						window.scrollTo({
							top: offsetPosition,
							behavior: "smooth",
						});
					}
				} else {
					scrollToSection(sectionId);
				}
			}, 500);
		}
	};

	// Helper function to check if a nav item is active
	const isActive = (pagePath: string, sectionId?: string) => {
		if (sectionId) {
			// For section buttons, check if on home page and section is active
			return pathname === "/" && activeSection === sectionId;
		}
		// For Home page link, check if pathname matches AND no section is active (hero section)
		if (pagePath === "/") {
			return pathname === "/" && activeSection === "";
		}
		// For other page links, check if pathname matches
		return pathname === pagePath;
	};

	// Check if we're on a white background page
	const isWhiteBackgroundPage =
		pathname === "/terms-of-service" ||
		pathname === "/privacy-policy" ||
		pathname === "/signup" ||
		pathname === "/forgot-password" ||
		pathname === "/dashboard" ||
		pathname === "/contact-us" ||
		pathname === "/profile";

	// Check if we're on terms-of-service or privacy-policy (use dark green primary)
	const useDarkGreenPrimary =
		pathname === "/terms-of-service" ||
		pathname === "/privacy-policy" ||
		pathname === "/forgot-password";

	// Active color class based on page
	const activeColorClass = useDarkGreenPrimary
		? "text-brand-green-dark"
		: "text-brand-green";

	// Active underline color class based on page
	const activeUnderlineClass = useDarkGreenPrimary
		? "bg-brand-green-dark"
		: "bg-brand-green";

	// Close mobile menu when route changes
	useEffect(() => {
		setMobileMenuOpen(false);
	}, [pathname]);

	const navItemClass =
		"whitespace-nowrap text-sm xl:text-base font-medium transition-all duration-300 relative group px-1.5 xl:px-2 py-1 rounded shrink-0";

	return (
		<header
			style={{ minHeight: "5rem" }}
			className={cn(
				"fixed top-12 left-1/2 -translate-x-1/2 z-[1100] w-max max-w-[calc(100vw-2rem)] px-4",
				className
			)}
		>
			<div
				className={cn(
					"flex w-max max-w-full min-w-0 items-center gap-3 sm:gap-4 md:gap-5 lg:gap-6 h-[4.5rem] px-4 sm:px-5 md:px-6 rounded-[5.3125rem] overflow-x-auto overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
					isWhiteBackgroundPage
						? "bg-white border border-gray-200 shadow-lg"
						: "border border-white-30 glass-effect shadow-glass"
				)}
				style={{
					boxShadow: isWhiteBackgroundPage
						? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
						: "0 0.25rem 2.11875rem 0.625rem rgba(0, 0, 0, 0.09)",
				}}
			>
				{/* Logo */}
				<Link
					href="/"
					className="flex shrink-0 items-center gap-2 whitespace-nowrap"
					data-testid="nav-logo"
				>
					<Image
						src={LogoIcon}
						alt="Tax Lien Vest Logo"
						width={38}
						height={38}
						className="w-[2.375rem] h-[2.375rem] shrink-0"
					/>
					<span
						className={cn(
							"text-lg sm:text-xl font-semibold tracking-wide font-bricolage hidden sm:inline",
							isWhiteBackgroundPage
								? "text-[#101828]"
								: "text-white"
						)}
					>
						Tax Lien Vest
					</span>
				</Link>

				{/* Desktop nav — hugs content; pill scrolls only if narrower than viewport */}
				<nav
					className="hidden lg:flex shrink-0 items-center py-1"
					aria-label="Main"
				>
					<div className="flex flex-nowrap items-center gap-x-1 xl:gap-x-2 2xl:gap-x-4">
						<Link
							href="/"
							className={cn(
								navItemClass,
								isActive("/")
									? activeColorClass
									: isWhiteBackgroundPage
										? "text-[#475467] hover:text-brand-green-dark hover:bg-gray-50"
										: "text-white hover:text-brand-green"
							)}
							data-testid="nav-home"
						>
							Home
							<span
								className={cn(
									"absolute bottom-0 left-2 right-2 h-0.5 transition-all duration-300",
									activeUnderlineClass,
									isActive("/")
										? "w-full"
										: "w-0 group-hover:w-full"
								)}
							></span>
						</Link>
						<button
							onClick={() => handleSectionClick("how-it-works")}
							className={cn(
								navItemClass,
								isActive("/", "how-it-works")
									? activeColorClass
									: isWhiteBackgroundPage
										? "text-[#475467] hover:text-brand-green-dark hover:bg-gray-50"
										: "text-white hover:text-brand-green"
							)}
							data-testid="nav-how-it-works"
						>
							How it works
							<span
								className={cn(
									"absolute bottom-0 left-2 right-2 h-0.5 transition-all duration-300",
									activeUnderlineClass,
									isActive("/", "how-it-works")
										? "w-full"
										: "w-0 group-hover:w-full"
								)}
							></span>
						</button>
						<button
							onClick={() => handleSectionClick("payment")}
							className={cn(
								navItemClass,
								isActive("/", "payment")
									? activeColorClass
									: isWhiteBackgroundPage
										? "text-[#475467] hover:text-brand-green-dark hover:bg-gray-50"
										: "text-white hover:text-brand-green"
							)}
							data-testid="nav-payment"
						>
							Payment
							<span
								className={cn(
									"absolute bottom-0 left-2 right-2 h-0.5 transition-all duration-300",
									activeUnderlineClass,
									isActive("/", "payment")
										? "w-full"
										: "w-0 group-hover:w-full"
								)}
							></span>
						</button>
						<button
							style={{ cursor: "pointer" }}
							onClick={() => handleSectionClick("contact")}
							className={cn(
								navItemClass,
								isActive("/", "contact")
									? activeColorClass
									: isWhiteBackgroundPage
										? "text-[#475467] hover:text-brand-green-dark hover:bg-gray-50"
										: "text-white hover:text-brand-green"
							)}
							data-testid="nav-resources"
						>
							Contact Us
							<span
								className={cn(
									"absolute bottom-0 left-2 right-2 h-0.5 transition-all duration-300",
									activeUnderlineClass,
									isActive("/", "contact")
										? "w-full"
										: "w-0 group-hover:w-full"
								)}
							></span>
						</button>
						<Link
							href="/faq"
							className={cn(
								navItemClass,
								isActive("/faq")
									? activeColorClass
									: isWhiteBackgroundPage
										? "text-[#475467] hover:text-brand-green-dark hover:bg-gray-50"
										: "text-white hover:text-brand-green"
							)}
							data-testid="nav-faq"
						>
							FAQ
							<span
								className={cn(
									"absolute bottom-0 left-2 right-2 h-0.5 transition-all duration-300",
									activeUnderlineClass,
									isActive("/faq")
										? "w-full"
										: "w-0 group-hover:w-full"
								)}
							></span>
						</Link>
						<Link
							href="/about"
							className={cn(
								navItemClass,
								isActive("/about")
									? activeColorClass
									: isWhiteBackgroundPage
										? "text-[#475467] hover:text-brand-green-dark hover:bg-gray-50"
										: "text-white hover:text-brand-green"
							)}
						>
							About Us
							<span
								className={cn(
									"absolute bottom-0 left-2 right-2 h-0.5 transition-all duration-300",
									activeUnderlineClass,
									isActive("/about")
										? "w-full"
										: "w-0 group-hover:w-full"
								)}
							></span>
						</Link>
						<Link
							href="/playground"
							className={cn(
								navItemClass,
								isActive("/playground")
									? activeColorClass
									: isWhiteBackgroundPage
										? "text-[#475467] hover:text-brand-green-dark hover:bg-gray-50"
										: "text-white hover:text-brand-green"
							)}
							data-testid="nav-playground"
						>
							Playground
							<span
								className={cn(
									"absolute bottom-0 left-2 right-2 h-0.5 transition-all duration-300",
									activeUnderlineClass,
									isActive("/playground")
										? "w-full"
										: "w-0 group-hover:w-full"
								)}
							></span>
						</Link>
					</div>
				</nav>

				{/* Desktop CTAs */}
				<motion.div
					className="hidden lg:flex shrink-0 flex-nowrap items-center gap-2 xl:gap-3"
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
				>
					<motion.a
						href="/imagery"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						className={cn(
							"font-inter font-semibold leading-tight text-sm xl:text-base px-3 xl:px-4 2xl:px-5 py-2 xl:py-2.5 rounded-[2.25rem] transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-1.5 xl:gap-2",
							isWhiteBackgroundPage
								? "border-2 border-brand-green-dark text-[#12301b] bg-brand-green-light/40 hover:bg-brand-green hover:border-brand-green-dark shadow-sm"
								: "border-2 border-brand-green text-white/95 bg-[rgba(8,18,12,0.55)] backdrop-blur-sm hover:bg-brand-green hover:text-[#12301b] hover:border-brand-green shadow-[0_0_22px_rgba(167,255,94,0.28)]"
						)}
						data-testid="nav-order-property-photos"
					>
						<Camera
							className="h-3.5 w-3.5 xl:h-[1.05rem] xl:w-[1.05rem] shrink-0"
							aria-hidden
						/>
						Order property photos
					</motion.a>
					{isLoggedIn ? (
						<motion.a
							href="/dashboard"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							className="font-inter font-semibold leading-tight text-sm xl:text-base text-[#12301b] px-3 xl:px-5 2xl:px-7 py-2 xl:py-2.5 rounded-[2.25rem] bg-[#a7ff5e] hover:bg-[#cbf7a6] transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-1.5 xl:gap-2"
							data-testid="nav-dashboard"
						>
							<LayoutDashboard
								className="h-3.5 w-3.5 xl:h-[1.05rem] xl:w-[1.05rem] shrink-0"
								aria-hidden
							/>
							Go to Dashboard
						</motion.a>
					) : (
						<>
							<motion.a
								href="/login"
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								className={
									isWhiteBackgroundPage
										? "font-inter font-semibold leading-tight text-sm xl:text-base text-[#12301b] px-3 xl:px-4 py-2 xl:py-2.5 rounded-[2.25rem] border border-[rgba(0,0,0,0.2)] bg-[rgba(0,0,0,0.05)] hover:bg-[rgba(0,0,0,0.08)] transition-colors cursor-pointer whitespace-nowrap"
										: "font-inter font-semibold leading-tight text-sm xl:text-base text-white px-3 xl:px-4 py-2 xl:py-2.5 rounded-[2.25rem] border border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.12)] transition-colors cursor-pointer whitespace-nowrap"
								}
								data-testid="nav-login"
							>
								Login
							</motion.a>
							<motion.a
								href="/signup"
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								className="font-inter font-semibold leading-tight text-sm xl:text-base text-[#12301b] px-3 xl:px-4 py-2 xl:py-2.5 rounded-[2.25rem] bg-[#a7ff5e] hover:bg-[#cbf7a6] transition-colors cursor-pointer whitespace-nowrap"
								data-testid="nav-signup"
							>
								Sign Up
							</motion.a>
						</>
					)}
				</motion.div>

				{/* Mobile Menu Button */}
				<button
					onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
					className={cn(
						"shrink-0 lg:hidden p-2 rounded-lg transition-colors",
						isWhiteBackgroundPage
							? "text-[#101828] hover:bg-gray-100"
							: "text-white hover:bg-white/10"
					)}
					aria-label="Toggle menu"
				>
					{mobileMenuOpen ? (
						<X className="w-6 h-6" />
					) : (
						<Menu className="w-6 h-6" />
					)}
				</button>
			</div>

			{/* Mobile Menu Overlay */}
			<AnimatePresence>
				{mobileMenuOpen && (
					<>
						{/* Backdrop */}
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="inset-0 bg-black/50 z-[45] lg:hidden"
							onClick={() => setMobileMenuOpen(false)}
						/>
						{/* Menu Panel */}
						<motion.div
							initial={{ x: "100%" }}
							animate={{ x: 0 }}
							exit={{ x: "100%" }}
							transition={{ type: "tween", duration: 0.3 }}
							className={cn(
								"top-[6.5rem] left-4 right-4 min-w-[15rem] max-w-[25rem] rounded-[1.5rem] shadow-2xl z-[60] overflow-hidden",
								isWhiteBackgroundPage
									? "bg-white"
									: "bg-[#16251c] border border-white/20"
							)}
						>
							<div className="p-6 space-y-4 max-h-[calc(100vh-7.5rem)] overflow-y-auto">
								{/* Navigation Links */}
								<div className="space-y-2">
									<Link
										href="/"
										onClick={() => setMobileMenuOpen(false)}
										className={cn(
											"block px-4 py-3 rounded-lg text-base font-medium transition-colors",
											isActive("/")
												? activeColorClass +
														" bg-opacity-10"
												: isWhiteBackgroundPage
													? "text-[#475467] hover:bg-gray-50"
													: "text-white hover:bg-white/10"
										)}
									>
										Home
									</Link>
									<button
										onClick={() => {
											handleSectionClick("how-it-works");
											setMobileMenuOpen(false);
										}}
										className={cn(
											"block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors",
											isActive("/", "how-it-works")
												? activeColorClass +
														" bg-opacity-10"
												: isWhiteBackgroundPage
													? "text-[#475467] hover:bg-gray-50"
													: "text-white hover:bg-white/10"
										)}
										data-testid="nav-how-it-works"
									>
										How it works
									</button>
									<button
										onClick={() => {
											handleSectionClick("payment");
											setMobileMenuOpen(false);
										}}
										className={cn(
											"block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors",
											isActive("/", "payment")
												? activeColorClass +
														" bg-opacity-10"
												: isWhiteBackgroundPage
													? "text-[#475467] hover:bg-gray-50"
													: "text-white hover:bg-white/10"
										)}
										data-testid="nav-payment"
									>
										Payment
									</button>
									<button
										onClick={() => {
											handleSectionClick("contact");
											setMobileMenuOpen(false);
										}}
										className={cn(
											"block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors",
											isActive("/", "contact")
												? activeColorClass +
														" bg-opacity-10"
												: isWhiteBackgroundPage
													? "text-[#475467] hover:bg-gray-50"
													: "text-white hover:bg-white/10"
										)}
										data-testid="nav-resources"
									>
										Contact Us
									</button>
									<Link
										href="/faq"
										onClick={() => setMobileMenuOpen(false)}
										className={cn(
											"block px-4 py-3 rounded-lg text-base font-medium transition-colors",
											isActive("/faq")
												? activeColorClass +
														" bg-opacity-10"
												: isWhiteBackgroundPage
													? "text-[#475467] hover:bg-gray-50"
													: "text-white hover:bg-white/10"
										)}
									>
										FAQ
									</Link>
									<Link
										href="/about"
										onClick={() => setMobileMenuOpen(false)}
										className={cn(
											"block px-4 py-3 rounded-lg text-base font-medium transition-colors",
											isActive("/about")
												? activeColorClass +
														" bg-opacity-10"
												: isWhiteBackgroundPage
													? "text-[#475467] hover:bg-gray-50"
													: "text-white hover:bg-white/10"
										)}
									>
										About Us
									</Link>
									<Link
										href="/playground"
										onClick={() => setMobileMenuOpen(false)}
										className={cn(
											"block px-4 py-3 rounded-lg text-base font-medium transition-colors",
											isActive("/playground")
												? activeColorClass +
														" bg-opacity-10"
												: isWhiteBackgroundPage
													? "text-[#475467] hover:bg-gray-50"
													: "text-white hover:bg-white/10"
										)}
									>
										Playground
									</Link>
								</div>

								{/* Auth / Dashboard Buttons */}
								<div className="pt-4 border-t space-y-3">
									<Link
										href="/imagery"
										onClick={() => setMobileMenuOpen(false)}
										className={cn(
											"flex items-center justify-center gap-2 text-center px-4 py-3 rounded-[2.25rem] text-base font-semibold active:scale-[0.98] transition-all",
											isWhiteBackgroundPage
												? "border-2 border-brand-green-dark text-[#12301b] bg-brand-green-light/50 hover:bg-brand-green"
												: "border-2 border-brand-green text-[#edffde] bg-[rgba(8,18,12,0.45)] hover:bg-brand-green hover:text-[#12301b] shadow-[0_0_18px_rgba(167,255,94,0.22)]"
										)}
										data-testid="nav-order-property-photos-mobile"
									>
										<Camera
											className="h-[1.125rem] w-[1.125rem] shrink-0"
											aria-hidden
										/>
										Order property photos
									</Link>
									{isLoggedIn ? (
										<Link
											href="/dashboard"
											onClick={() =>
												setMobileMenuOpen(false)
											}
											className="flex items-center justify-center gap-2 text-center px-4 py-3 rounded-[2.25rem] text-base font-semibold text-[#12301b] bg-[#a7ff5e] hover:bg-[#cbf7a6] active:scale-[0.98] transition-all"
											data-testid="nav-dashboard-mobile"
										>
											<LayoutDashboard
												className="h-[1.125rem] w-[1.125rem] shrink-0"
												aria-hidden
											/>
											Go to Dashboard
										</Link>
									) : (
										<>
											<Link
												href="/login"
												onClick={() =>
													setMobileMenuOpen(false)
												}
												className={cn(
													"block text-center px-4 py-3 rounded-[2.25rem] text-base font-semibold transition-colors",
													isWhiteBackgroundPage
														? "text-[#12301b] border border-[rgba(0,0,0,0.2)] bg-[rgba(0,0,0,0.05)]"
														: "text-white border border-white/20 bg-white/5"
												)}
											>
												Login
											</Link>
											<Link
												href="/signup"
												onClick={() =>
													setMobileMenuOpen(false)
												}
												className="block text-center px-4 py-3 rounded-[2.25rem] text-base font-semibold text-[#12301b] bg-[#a7ff5e] hover:bg-[#cbf7a6] transition-colors"
											>
												Sign Up
											</Link>
										</>
									)}
								</div>
							</div>
						</motion.div>
					</>
				)}
			</AnimatePresence>
		</header>
	);
};

export default Header;
